import { useState } from "react";
import { ScrollView, Text, View, useWindowDimensions } from "react-native";
import { Check } from "lucide-react-native";
import {
  CompactActionButton,
  Dialog,
  DialogActions,
  DialogHeader,
  PrimaryButton,
  DateField,
  FieldMessage,
  Row,
  SectionTitle,
  semanticColors,
} from "@louise/moon-ui";
import { EMBER } from "@louise/moon-ui/theme/tokens";
import { useTranslations } from "@src/packages/i18n";
import { useCreateTestResult } from "@src/packages/test-results/hooks/useTestResults";
import { LabValueField } from "@src/packages/test-results/components/LabValueField";
import { TestResultDocumentAction } from "./TestResultDocumentAction";
import type { SelectedFile } from "@src/packages/document-vault/DocumentFileDropZone";
import {
  parseAmhUnit,
  useAmhResults,
  type AmhResult,
  type AmhUnit,
} from "@src/packages/homepage/utils/ovarianReserveInputs";
import { getTodayString, parseDateOrTimestamp } from "@src/packages/utils/date";

export type AmhSelection = {
  value: number;
  unit: AmhUnit;
  sourceId?: string;
};

type Props = {
  open: boolean;
  onClose: () => void;
  onSelect: (selection: AmhSelection) => void;
  onClear?: () => void;
  onOpenDocuments: (files: SelectedFile[]) => void;
  currentSourceId?: string | null;
  hasActiveAmh?: boolean;
};

function formatDate(iso: string | null): string {
  if (!iso) return "";
  const d = parseDateOrTimestamp(iso);
  if (Number.isNaN(d.getTime())) return "";
  return d.toLocaleDateString(undefined, {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
}

function ExistingResultRow({
  result,
  selected,
}: {
  result: AmhResult;
  selected: boolean;
}) {
  // The canonical Row on the input fill: a glass row's shadow is clipped by
  // the dialog's scroll area.
  return (
    <Row
      surface="input"
      title={`${result.value} ${result.unit}`}
      subtitle={result.date ? formatDate(result.date) : undefined}
      trailing={
        selected ? (
          <Check
            size={18}
            color={semanticColors.text.primary}
            strokeWidth={2.2}
          />
        ) : undefined
      }
    />
  );
}

export function AmhSourceSheet({
  open,
  onClose,
  onSelect,
  onClear,
  onOpenDocuments,
  currentSourceId,
  hasActiveAmh,
}: Props) {
  const t = useTranslations();
  const ns = "homepage.amh_source_sheet";
  const { height } = useWindowDimensions();
  const existing = useAmhResults();
  const createTestResult = useCreateTestResult();
  const latest = existing[0] ?? null;
  const dialogMaxHeight = Math.min(760, Math.max(420, height - 48));

  // Text, as the lab result field holds it; read as a number to save.
  const [valueText, setValueText] = useState("");
  const [unit, setUnit] = useState<AmhUnit>("ng/mL");
  const value = valueText === "" ? null : Number(valueText);
  const [date, setDate] = useState<string>(() => getTodayString());

  const canSave =
    value !== null &&
    Number.isFinite(value) &&
    value > 0 &&
    !createTestResult.isPending;

  const highValueWarning: "ng" | "pmol" | null =
    value === null
      ? null
      : unit === "ng/mL" && value > 15
        ? "ng"
        : unit === "pmol/L" && value > 100
          ? "pmol"
          : null;

  const handleAddNew = async () => {
    if (value === null || !Number.isFinite(value) || value <= 0) return;
    try {
      await createTestResult.mutateAsync({
        name: "AMH",
        type: "blood",
        bloodTestCategory: "hormonal",
        patientValue: String(value),
        unit,
        valueType: "numeric",
        date: date || null,
      });
      onSelect({ value, unit });
      setValueText("");
      onClose();
    } catch {
      // surface via mutation error state; keep sheet open
    }
  };

  const handleOpenDocuments = (files: SelectedFile[]) => {
    onClose();
    onOpenDocuments(files);
  };

  return (
    <Dialog
      containerStyle={{ maxHeight: dialogMaxHeight }}
      visible={open}
      onClose={onClose}
      closeButton="minimal"
      surfaceStyle={{ maxHeight: dialogMaxHeight }}
      contentStyle={{
        paddingTop: 24,
        paddingHorizontal: 24,
        paddingBottom: 24,
        maxHeight: dialogMaxHeight,
      }}
    >
      <DialogHeader
        title={t(`${ns}.title`)}
        subtitle={t(
          `${ns}.${latest ? "subtitle_with_result" : "subtitle_empty"}`,
        )}
        style={{ marginBottom: 16 }}
      />
      <ScrollView
        showsVerticalScrollIndicator={false}
        keyboardShouldPersistTaps="handled"
        style={{ flexShrink: 1 }}
        contentContainerStyle={{ gap: 16, paddingBottom: 4 }}
      >
        <TestResultDocumentAction onOpenDocuments={handleOpenDocuments} />

        {latest ? (
          <View style={{ gap: 8 }}>
            <SectionTitle>{t(`${ns}.latest_section`)}</SectionTitle>
            <ExistingResultRow
              result={latest}
              selected={
                latest.id === currentSourceId || currentSourceId == null
              }
            />
            {existing.length > 1 ? (
              <View style={{ gap: 8, marginTop: 12 }}>
                <SectionTitle>{t(`${ns}.previous_section`)}</SectionTitle>
                {existing.slice(1).map((result) => (
                  <ExistingResultRow
                    key={result.id}
                    result={result}
                    selected={false}
                  />
                ))}
              </View>
            ) : null}
          </View>
        ) : null}

        <View style={{ gap: 8, marginTop: latest ? 20 : 0 }}>
          <SectionTitle>{t(`${ns}.manual_section`)}</SectionTitle>
          <View style={{ gap: 12 }}>
            {/* The same field as editing any lab result, so the two match. */}
            <LabValueField
              label={t(`${ns}.value_label`)}
              value={valueText}
              onChangeValue={setValueText}
              unit={unit}
              onChangeUnit={(next) => setUnit(parseAmhUnit(next))}
              marker="AMH"
              numeric
              placeholder="0.0"
            />
            <DateField
              label={t(`${ns}.date_label`)}
              value={date}
              onChange={setDate}
            />
          </View>
          {highValueWarning ? (
            <FieldMessage tone="warning" style={{ marginTop: 4 }}>
              {t(
                highValueWarning === "ng"
                  ? `${ns}.high_value_warning_ng`
                  : `${ns}.high_value_warning_pmol`,
              )}
            </FieldMessage>
          ) : null}
          {createTestResult.isError ? (
            <FieldMessage style={{ marginTop: 4 }}>
              {t(`${ns}.save_error`)}
            </FieldMessage>
          ) : null}
        </View>

        {hasActiveAmh && onClear ? (
          <CompactActionButton
            label={t(`${ns}.remove_amh`)}
            action={`${ns}.remove_amh`}
            variant="secondary"
            size="sm"
            onPress={() => {
              onClear();
              onClose();
            }}
            style={{ alignSelf: "flex-start", marginTop: 20 }}
          />
        ) : null}

        <Text
          className="font-dm-sans"
          style={{
            fontSize: 11,
            lineHeight: 15,
            color: EMBER.colors.textFaint,
            marginTop: 16,
          }}
        >
          {t(`${ns}.source_line`)}
        </Text>
      </ScrollView>
      <DialogActions style={{ marginTop: 16 }}>
        <PrimaryButton
          label={
            createTestResult.isPending
              ? t(`${ns}.saving`)
              : t(`${ns}.save_and_use`)
          }
          onPress={handleAddNew}
          loading={createTestResult.isPending}
          disabled={!canSave}
        />
      </DialogActions>
    </Dialog>
  );
}
